import React, { useState } from 'react';
import {
  AppLayout,
  SideNavigation,
  Toggle,
  TopNavigation,
  type SideNavigationProps,
} from '@cloudscape-design/components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';

const NAV_ITEMS: SideNavigationProps.Item[] = [
  { type: 'section', text: 'Onboarding', items: [
    { type: 'link', text: 'Fulfillment', href: '/ops/fulfillment' },
    { type: 'link', text: 'Tenants', href: '/ops/tenants' },
    { type: 'link', text: 'Role Deployments', href: '/ops/deployments' },
    { type: 'link', text: 'Product Deployments', href: '/ops/product-deployments' },
  ]},
  { type: 'section', text: 'Platform Modules', items: [
    { type: 'link', text: 'Onboarding Manager', href: '/ops/onboarding' },
    { type: 'link', text: 'Landing Pages', href: '/ops/landing-pages' },
    { type: 'link', text: 'Demo Catalog', href: '/ops/demos' },
    { type: 'link', text: 'ZTrust Control', href: '/ops/ztrust' },
    { type: 'link', text: 'Chat Inbox', href: '/ops/chat' },
  ]},
  { type: 'section', text: 'Operations', items: [
    { type: 'link', text: 'AEOS Ops', href: '/ops/aeos' },
    { type: 'link', text: 'Stack Versions', href: '/ops/versions' },
    { type: 'link', text: 'Customer Deployments', href: '/ops/customer-deployments' },
    { type: 'link', text: 'Health', href: '/ops/health' },
  ]},
  { type: 'divider' },
  { type: 'section', text: 'Products', items: [
    { type: 'link', text: 'PC3 Console', href: '/pc3' },
    { type: 'link', text: 'ACP Panels', href: '/acp-panels' },
  ]},
  { type: 'divider' },
  { type: 'link', text: 'Settings', href: '/ops/settings' },
];

interface OpsLayoutProps {
  children: React.ReactNode;
}

export function OpsLayout({ children }: OpsLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [navOpen, setNavOpen] = useState(true);
  const [darkMode, setDarkMode] = useState(() => document.body.classList.contains('awsui-dark-mode'));

  function toggleDarkMode(checked: boolean) {
    setDarkMode(checked);
    if (checked) {
      document.body.classList.add('awsui-dark-mode');
      localStorage.setItem('peregrine-dark-mode', 'dark');
    } else {
      document.body.classList.remove('awsui-dark-mode');
      localStorage.setItem('peregrine-dark-mode', 'light');
    }
  }

  const userName = user?.name || user?.email || 'Operator';

  return (
    <>
      <div id="top-nav">
        <TopNavigation
          identity={{
            href: '/ops/fulfillment',
            title: 'g/d/n/a Peregrine',
            logo: { src: '/peregrine_black.png', alt: 'Peregrine' },
          }}
          utilities={[
            {
              type: 'menu-dropdown',
              text: 'Peregrine Ops',
              items: [
                { id: 'peregrine-ops', text: '🟢 Peregrine Ops' },
                { id: 'pc3-console', text: '🔵 PC3 Console' },
              ],
              onItemClick: ({ detail }: { detail: { id: string } }) => {
                if (detail.id === 'peregrine-ops') navigate('/ops/fulfillment');
                if (detail.id === 'pc3-console') navigate('/pc3');
              },
              disableUtilityCollapse: true,
            },
            {
              type: 'menu-dropdown',
              text: userName,
              iconName: 'user-profile',
              items: [
                { id: 'profile', text: `${user?.email || ''}` },
                { id: 'groups', text: `Groups: ${user?.groups?.join(', ') || 'none'}` },
                { id: 'darkmode', text: darkMode ? '☀️ Light Mode' : '🌙 Dark Mode' },
                { id: 'signout', text: 'Sign out' },
              ],
              onItemClick: ({ detail }) => {
                if (detail.id === 'signout') signOut();
                if (detail.id === 'darkmode') toggleDarkMode(!darkMode);
              },
            },
          ]}
        />
      </div>
      <AppLayout
        navigation={
          <SideNavigation
            header={{ text: 'Operations', href: '/ops/tenants' }}
            activeHref={location.pathname}
            items={NAV_ITEMS}
            onFollow={(event) => {
              event.preventDefault();
              navigate(event.detail.href);
            }}
          />
        }
        navigationOpen={navOpen}
        onNavigationChange={({ detail }) => setNavOpen(detail.open)}
        toolsHide
        content={children}
        headerSelector="#top-nav"
      />
    </>
  );
}
